import { useEffect } from 'react';
import { moment } from '@hotjar/webapp/lib/moment';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { Referral } from '@hotjar/webapp/domains/acquisition/referrals/types';
import { useCurrentUser } from '@hotjar/user';
import { useUserReferrals } from '@hotjar/webapp/hooks/useReferrals';
import {
  Avatar,
  Card,
  Divider,
  Text,
  Loader,
  Icon,
  Table,
  Badge,
  Tooltip,
  tokens,
} from '@hotjar/hotsauce';
import { BASIC, BUSINESS, PLUS, SCALE } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import bgImage from '@hotjar/shared-assets/images/referrals-bg.png';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import { page } from '@hotjar/webapp/domains/common/tracking/segment';
import { HowItWorks } from './howItWorks';

const Wrapper = styled.div`
  padding: ${tokens.spacer.x4} ${tokens.spacer.x7};
`;

const TextWrap = styled.div`
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2} 0 ${tokens.spacer.x2};
`;

const TextWithMargin = styled(Text)`
  display: inline-block;
  margin-bottom: ${tokens.spacer.x1};
  margin-right: ${tokens.spacer.x0};
`;

const HeadingWrap = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${tokens.spacer.x5};
`;

const ColWrap = styled.div`
  display: flex;
  align-items: flex-start;

  @media ${tokens.breakpoint.maxWidth.l} {
    flex-direction: column;
  }
`;

const HeadersWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${tokens.spacer.x3};
`;

const ReferralsBlock = styled.div`
  flex: 1 1 auto;
  max-width: 795px;
  margin-right: ${tokens.spacer.x3};
  border-radius: ${tokens.spacer.x0};
  border: 1px solid ${tokens.color.ui_border_light};
  ${tokens.elevation(1)};

  @media ${tokens.breakpoint.maxWidth.l} {
    margin-bottom: ${tokens.spacer.x3};
  }
`;

const TableWrap = styled.div`
  padding: ${tokens.spacer.x2} ${tokens.spacer.x3} ${tokens.spacer.x3};
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

const StyledText = styled(Text)`
  text-shadow: none;
`;

const StyledImage = styled.div`
  width: 100%;
  height: 91px;
  background-image: url(${bgImage});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x3};
  padding: 0 ${tokens.spacer.x2};
  display: flex;
  justify-content: space-evenly;
  align-items: center;
`;
const StyledCard = styled(Card)`
  border: 1px solid ${tokens.color.ui_border_light};
  ${tokens.elevation(1)};

  @media ${tokens.breakpoint.maxWidth.m} {
    flex: 1 1 auto;
  }
`;

const LoaderWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
`;

const ReferralStep = styled.div`
  display: flex;
  flex-direction: column;
`;

const PLANS_COLORS_MAP = {
  [BASIC]: '#8D96A5', // bermuda
  [BUSINESS]: '#EFB60C', // beer
  [PLUS]: '#AB63C7', // lilac
  [SCALE]: '#00A2F2', // azure
};

const ArrowIcon = () => (
  <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.96 3.69205C11.01 3.57032 11.01 3.43378 10.96 3.31205C10.9362 3.25067 10.9005 3.1946 10.855 3.14705L7.855 0.147046C7.80838 0.100427 7.75304 0.0634461 7.69212 0.0382159C7.63121 0.0129858 7.56593 0 7.5 0C7.36685 0 7.23915 0.0528939 7.145 0.147046C7.05085 0.241198 6.99795 0.368895 6.99795 0.502046C6.99795 0.635197 7.05085 0.762894 7.145 0.857046L9.295 3.00205H0.5C0.367392 3.00205 0.240215 3.05472 0.146447 3.14849C0.0526785 3.24226 0 3.36944 0 3.50205C0 3.63465 0.0526785 3.76183 0.146447 3.8556C0.240215 3.94937 0.367392 4.00205 0.5 4.00205H9.295L7.145 6.14705C7.09814 6.19353 7.06094 6.24883 7.03555 6.30976C7.01017 6.37069 6.9971 6.43604 6.9971 6.50205C6.9971 6.56805 7.01017 6.63341 7.03555 6.69433C7.06094 6.75526 7.09814 6.81056 7.145 6.85705C7.19148 6.90391 7.24678 6.94111 7.30771 6.96649C7.36864 6.99188 7.43399 7.00495 7.5 7.00495C7.56601 7.00495 7.63136 6.99188 7.69229 6.96649C7.75322 6.94111 7.80852 6.90391 7.855 6.85705L10.855 3.85705C10.9005 3.80949 10.9362 3.75342 10.96 3.69205Z"
      fill="black"
      fillOpacity="0.89"
    />
  </svg>
);

export const ReferralsPage = (): JSX.Element => {
  const { t } = useTranslation();
  const { data: user, isLoading: isUserLoading } = useCurrentUser();
  const { data: referralsData, isLoading: isReferralsLoading } = useUserReferrals(
    user?.id?.toString(),
  );

  usePageTitle('Refer & Earn - Hotjar');

  useEffect(() => {
    page('profile.referrals');
  }, []);

  const referralsNotLoaded = isUserLoading || isReferralsLoading;

  return referralsNotLoaded ? (
    <LoaderWrapper>
      <Loader size="xl" />
    </LoaderWrapper>
  ) : (
    <Wrapper>
      <HeadingWrap>
        <Avatar size="xl" src={user?.avatar || undefined} userName={user?.name || ''} />
        <HeadersWrap>
          <Text as="h1" textType="header3" data-testid="referralsHeader">
            {user?.name}
            {t('common:referrals.title')}
          </Text>
          <Text as="span" textType="body2" textColor="text_light">
            {t('common:referrals.subtitle')}
          </Text>
        </HeadersWrap>
      </HeadingWrap>
      <ColWrap>
        {referralsData?.potentialReferralsCount || referralsData?.referralsCount ? (
          <ReferralsBlock data-testid="yourReferralsBlock">
            <TextWrap>
              <Text as="p" textType="subtitle1">
                {t('common:referrals.your-referrals.title')}
              </Text>
              <Text as="span" textType="caption" textColor="text_light">
                {t('common:referrals.your-referrals.subtitle')}
              </Text>
            </TextWrap>
            <Divider />
            <TableWrap>
              <div>
                <StyledImage>
                  <ReferralStep>
                    <div>
                      <TextWithMargin as="span" textType="caption" textColor="text_light">
                        {t('common:referrals.your-referrals.trial-heading')}
                      </TextWithMargin>
                      <Tooltip text={t('common:referrals.your-referrals.trial-accounts-tooltip')}>
                        <StyledIcon icon="info" color="text_light" />
                      </Tooltip>
                    </div>
                    <StyledText as="h1" textType="header4" data-testid="potentialReferralsCount">
                      {referralsData?.potentialReferralsCount}
                    </StyledText>
                  </ReferralStep>
                  <ReferralStep>
                    <ArrowIcon />
                  </ReferralStep>
                  <ReferralStep>
                    <div>
                      <TextWithMargin as="span" textType="caption" textColor="text_light">
                        {t('common:referrals.your-referrals.referrals-heading')}
                      </TextWithMargin>
                      <Tooltip text={t('common:referrals.your-referrals.referrals-tooltip')}>
                        <StyledIcon icon="info" color="text_light" />
                      </Tooltip>
                    </div>
                    <StyledText
                      as="h4"
                      textColor="text_default"
                      textType="header4"
                      data-testid="referralsCount"
                    >
                      {referralsData?.referralsCount}
                    </StyledText>
                  </ReferralStep>
                  <ReferralStep>
                    <ArrowIcon />
                  </ReferralStep>
                  <ReferralStep>
                    <div>
                      <TextWithMargin as="span" textType="caption" textColor="text_light">
                        {t('common:referrals.your-referrals.reward-heading')}
                      </TextWithMargin>
                      <Tooltip text={t('common:referrals.your-referrals.reward-tooltip')}>
                        <StyledIcon icon="info" color="text_light" />
                      </Tooltip>
                    </div>
                    <StyledText
                      as="h4"
                      data-testid="rewardSum"
                      textType="header4"
                      textColor={
                        (referralsData?.rewardSum || 0) > 0 ? 'text_success' : 'text_default'
                      }
                    >
                      ${referralsData?.rewardSum}
                    </StyledText>
                  </ReferralStep>
                </StyledImage>
              </div>
              {referralsData?.referralsCount === 0 ? (
                <div>
                  <Text as="span" textType="body2">
                    {t('common:referrals.your-referrals.no-referrals.1')}&nbsp;
                  </Text>
                  <Text as="span" textType="subtitle2">
                    {referralsData?.potentialReferralsCount}{' '}
                    {t('common:referrals.your-referrals.no-referrals.2')}&nbsp;
                    {referralsData?.potentialReferralsCount > 1 ? 'accounts' : 'account'}
                  </Text>
                  <Text as="span" textType="body2">
                    {t('common:referrals.your-referrals.no-referrals.3')}
                  </Text>
                </div>
              ) : (
                <Table
                  caption="Referrals list"
                  tableName="Referrals"
                  data-testid="referralsTable"
                  withStickyLastColumn={false}
                >
                  <Table.Head>
                    <Table.HeadRow>
                      <Table.HeadCell title="account type" aria-label="">
                        {t('common:referrals.your-referrals.table.account-type')}
                      </Table.HeadCell>
                      <Table.HeadCell title="accounts created" aria-label="">
                        {t('common:referrals.your-referrals.table.plan')}
                      </Table.HeadCell>
                      <Table.HeadCell title="reward" aria-label="">
                        {t('common:referrals.your-referrals.table.reward')}
                      </Table.HeadCell>
                      <Table.HeadCell title="last updated" aria-label="">
                        {t('common:referrals.your-referrals.table.date-created')}
                      </Table.HeadCell>
                    </Table.HeadRow>
                  </Table.Head>
                  <Table.Body>
                    {(referralsData?.referrals || []).map((referral: Referral) => (
                      <Table.Row key={referral.referralUserId} data-testid="referralsTableRow">
                        <Table.Cell>
                          <Text as="span" textColor="text_success">
                            Paid account
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge kind="custom" color={PLANS_COLORS_MAP[referral.plan]}>
                            {referral.plan}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Text as="span" textColor="text_success">
                            {referral.reward ? `+${referral.reward}$` : ''}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          {moment(referral.subscriptionCreated).format('DD/MM/YYYY')}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </TableWrap>
          </ReferralsBlock>
        ) : null}
        <StyledCard>
          <Card.Header title={t('common:referrals.how-it-works.card-title')} />
          <Card.Body>
            <HowItWorks referralCode={user?.referralCode ?? ''} />
          </Card.Body>
        </StyledCard>
      </ColWrap>
    </Wrapper>
  );
};
